// frontend/src/pages/components/SperrlisteFormDialog.tsx
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid, MenuItem,
  FormControlLabel, Switch, Typography
} from '@mui/material';
import { getApiUrl, authFetch } from '../../config';

// Auswählbare Kategorien (Label) → gespeicherter kategorie-Wert.
const KATEGORIE_OPTIONS: { value: string; label: string }[] = [
  { value: 'BLACKLIST-BOESCHE-INTERN-SUCHT', label: 'Spielsucht' },
  { value: 'BLACKLIST-BOESCHE-INTERN', label: 'Intern' },
];

interface Props {
  open: boolean;
  record: any | null;
  onClose: () => void;
  onSave: () => void;
}

export default function SperrlisteFormDialog({ open, record, onClose, onSave }: Props) {
  const isEdit = !!record?.id;

  const [form, setForm] = useState({
    kategorie: '', vnm: '', nnm: '', str: '', hnr: '', plz: '', ort: '',
    lkn: 'D', lnd: '', akn: '', mandant: '', ttl: '',
    tns: '', hns: '', ema: '',
    blz: '', kto: '', iban: '', bic: '',
    aid: '',
    match_type: 'exact' as 'exact' | 'prefix' | 'phonetic',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (record) {
      setForm({
        kategorie: record.kategorie || '',
        vnm: record.vnm || '', nnm: record.nnm || '',
        str: record.str || '', hnr: record.hnr || '',
        plz: record.plz || '', ort: record.ort || '',
        lkn: record.lkn || 'D', lnd: record.lnd || '',
        akn: record.akn || '', mandant: record.mandant || '', ttl: record.ttl || '',
        tns: record.tns || '', hns: record.hns || '', ema: record.ema || '',
        blz: record.blz || '', kto: record.kto || '',
        iban: record.iban || '', bic: record.bic || '',
        aid: record.aid ? String(record.aid) : '',
        match_type: record.match_type || 'exact',
        active: record.active !== false && record.active !== 0,
      });
    } else {
      setForm({
        kategorie: 'BLACKLIST-BOESCHE-INTERN-SUCHT', vnm: '', nnm: '', str: '', hnr: '', plz: '', ort: '',
        lkn: 'D', lnd: '', akn: '', mandant: '', ttl: '',
        tns: '', hns: '', ema: '',
        blz: '', kto: '', iban: '', bic: '',
        aid: '', match_type: 'exact', active: true
      });
    }
    setError('');
  }, [record, open]);

  const handleSave = async () => {
    if (!form.nnm.trim()) {
      setError('Nachname ist Pflichtfeld');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        aid: form.aid ? parseInt(form.aid) : null,
      };

      const url = isEdit
        ? getApiUrl(`/sperrliste/${record.id}`)
        : getApiUrl('/sperrliste');
      const method = isEdit ? 'PUT' : 'POST';

      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      onSave();
    } catch (err) {
      setError((err as Error).message);
    }
    setSaving(false);
  };

  const set = (field: string) => (e: any) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Sperrliste-Eintrag bearbeiten' : 'Neuer Sperrliste-Eintrag'}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Kategorie */}
          <Grid size={{ xs: 12, sm: 9 }}>
            <TextField select label="Kategorie" size="small" fullWidth value={form.kategorie} onChange={set('kategorie')}>
              {KATEGORIE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />}
              label="Aktiv"
            />
          </Grid>

          {/* Identitaet Zeile 1: Anrede + Titel */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select label="Anrede" size="small" fullWidth value={form.akn} onChange={set('akn')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="Herr">Herr</MenuItem>
              <MenuItem value="Frau">Frau</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Titel" size="small" fullWidth value={form.ttl} onChange={set('ttl')} />
          </Grid>

          {/* Identitaet Zeile 2: Vorname + Nachname */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Vorname" size="small" fullWidth value={form.vnm} onChange={set('vnm')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Nachname *" size="small" fullWidth value={form.nnm} onChange={set('nnm')} required />
          </Grid>

          {/* Identitaet Zeile 3: Mandant + KundenNr */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField select label="Mandant" size="small" fullWidth value={form.mandant} onChange={set('mandant')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="BOK">BOK</MenuItem>
              <MenuItem value="BOH">BOH</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="KundenNr (AID)" size="small" fullWidth value={form.aid} onChange={set('aid')} type="number" />
          </Grid>

          {/* Adresse */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Straße" size="small" fullWidth value={form.str} onChange={set('str')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField label="HNR" size="small" fullWidth value={form.hnr} onChange={set('hnr')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField label="PLZ" size="small" fullWidth value={form.plz} onChange={set('plz')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <TextField label="Ort" size="small" fullWidth value={form.ort} onChange={set('ort')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Land" size="small" fullWidth value={form.lnd} onChange={set('lnd')} />
          </Grid>

          {/* Kontakt */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Telefon" size="small" fullWidth value={form.tns} onChange={set('tns')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Handy" size="small" fullWidth value={form.hns} onChange={set('hns')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="E-Mail" size="small" fullWidth value={form.ema} onChange={set('ema')} />
          </Grid>

          {/* Bank */}
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="IBAN" size="small" fullWidth value={form.iban} onChange={set('iban')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="BIC" size="small" fullWidth value={form.bic} onChange={set('bic')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="BLZ" size="small" fullWidth value={form.blz} onChange={set('blz')} />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="Konto" size="small" fullWidth value={form.kto} onChange={set('kto')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Speichere...' : (isEdit ? 'Speichern' : 'Anlegen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
